import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import { formatNumber, formatPrice } from "@/lib/format-amount";
import {
  CalendarIcon,
  RefreshCwIcon,
  SettingsIcon,
  XCircleIcon,
} from "lucide-react";
import { NavLink } from "react-router-dom";

interface ApiCreditsSubscription {
  packageId: string;
  credits: number;
  priceMonthly: number;
  currentPeriodEndsAt: Date | null;
  cancelAtPeriodEnd: boolean;
}

interface CardApiCreditsSubscriptionProps {
  subscription: ApiCreditsSubscription;
  onChangePlan: () => void;
  onCancel: () => void;
  onResume: () => void;
  compact?: boolean;
}

export function CardApiCreditsSubscription({
  subscription,
  onChangePlan,
  onCancel,
  onResume,
  compact = false,
}: CardApiCreditsSubscriptionProps) {
  const { t } = useTranslation();

  const formatDate = (date: Date | null): string => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatCredits = formatNumber;

  if (compact) {
    return (
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-majorelle/10">
                <SettingsIcon className="w-4 h-4 text-majorelle" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{t("team.apiCredits.billing.title")}</p>
                  <Badge
                    variant={subscription.cancelAtPeriodEnd ? "warning" : "success"}
                    className="text-[10px]"
                  >
                    {subscription.cancelAtPeriodEnd
                      ? t("team.apiCredits.subscription.cancelling")
                      : t("team.apiCredits.subscription.active")}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatCredits(subscription.credits)} {t("team.apiCredits.subscription.creditsPerMonth")} —{" "}
                  {formatPrice(subscription.priceMonthly)}/{t("team.apiCredits.purchase.month")}
                </p>
              </div>
            </div>
            <NavLink
              to="/integrations/api"
              className="text-sm font-medium text-majorelle hover:underline"
            >
              {t("team.apiCredits.billing.manage")}
            </NavLink>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-majorelle/20">
      <CardContent className="p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <p className="font-medium">
                {formatCredits(subscription.credits)} {t("team.apiCredits.subscription.creditsPerMonth")}
              </p>
              <Badge variant={subscription.cancelAtPeriodEnd ? "warning" : "success"}>
                {subscription.cancelAtPeriodEnd
                  ? t("team.apiCredits.subscription.cancelling")
                  : t("team.apiCredits.subscription.active")}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {formatPrice(subscription.priceMonthly)}/{t("team.apiCredits.purchase.month")}
            </p>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarIcon className="w-3 h-3" />
              {subscription.cancelAtPeriodEnd
                ? t("team.apiCredits.subscription.cancelsOn", {
                    date: formatDate(subscription.currentPeriodEndsAt),
                  })
                : t("team.apiCredits.subscription.renewsOn", {
                    date: formatDate(subscription.currentPeriodEndsAt),
                  })}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onChangePlan}>
              <SettingsIcon className="w-3.5 h-3.5 mr-1.5" />
              {t("team.apiCredits.subscription.changePlan")}
            </Button>

            {subscription.cancelAtPeriodEnd ? (
              <Button variant="outline" size="sm" onClick={onResume}>
                <RefreshCwIcon className="w-3.5 h-3.5 mr-1.5" />
                {t("team.apiCredits.subscription.resume")}
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                    <XCircleIcon className="w-3.5 h-3.5 mr-1.5" />
                    {t("team.apiCredits.subscription.cancel")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("team.apiCredits.subscription.cancelConfirmTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("team.apiCredits.subscription.cancelConfirmDescription", {
                        date: formatDate(subscription.currentPeriodEndsAt),
                      })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("team.apiCredits.subscription.cancelKeepButton")}</AlertDialogCancel>
                    <AlertDialogAction variant="destructive" onClick={onCancel}>
                      {t("team.apiCredits.subscription.cancelConfirmButton")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
